import { useEffect, useState, type ReactElement } from 'react'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { AppDialog, Button, FamilyCard, MiniLoader } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { getEduMediaConsent, type EduMediaConsent } from '../../services/eduMediaConsentService'
import {
  getEduConsent,
  withdrawConsent,
  type EduConsentDoc,
} from '../../services/eduLearnerService'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import './familyLeave.css'

export interface FamilyConsentPanelProps {
  workspaceId: string
  learnerId: string
  onBack: () => void
}

export const FamilyConsentPanel = ({
  workspaceId,
  learnerId,
  onBack,
}: FamilyConsentPanelProps): ReactElement => {
  const { t } = useNls()
  const [consent, setConsent] = useState<EduMediaConsent | null>(null)
  const [eduConsent, setEduConsent] = useState<EduConsentDoc | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [withdrawing, setWithdrawing] = useState<boolean>(false)
  const [withdrawn, setWithdrawn] = useState<boolean>(false)
  const [consentLearnerId, setConsentLearnerId] = useState<string>(learnerId)

  if (consentLearnerId !== learnerId) {
    setConsentLearnerId(learnerId)
    setConsent(null)
    setEduConsent(null)
    setError(null)
    setLoading(true)
    setWithdrawn(false)
  }

  useEffect(() => {
    let cancelled = false

    Promise.all([
      getEduMediaConsent({ workspaceId, learnerId }).catch(() => ({ consent: null })),
      getEduConsent(workspaceId, learnerId).catch(() => null),
    ])
      .then(([mediaRes, eduConsentRes]) => {
        if (!cancelled) {
          setConsent(mediaRes?.consent ?? null)
          setEduConsent(eduConsentRes)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(mapEduServerError(err, t))
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId, learnerId, t])

  const isPublicTask = eduConsent?.basis === 'PUBLIC_TASK_OR_CONTRACT'

  const handleConfirmWithdraw = async () => {
    setShowConfirm(false)
    setWithdrawing(true)
    setError(null)
    try {
      await withdrawConsent(learnerId)
      setWithdrawn(true)
    } catch (err: unknown) {
      setError(mapEduServerError(err, t))
    } finally {
      setWithdrawing(false)
    }
  }

  return (
    <div className="familyConsentPanel" data-testid="family-consent-panel">
      <FamilyCard
        title={t('NLSFamilyMoreConsent')}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={onBack}
            data-testid="consent-back-btn"
            aria-label="Back"
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
          </Button>
        }
      >
        {loading ? (
          <div className="familyMoreTab__loading" data-testid="consent-loader">
            <MiniLoader />
          </div>
        ) : error ? (
          <div className="familyMoreTab__error" data-testid="consent-error">
            {error}
          </div>
        ) : (
          <div className="familyConsentPanel__content">
            {withdrawn ? (
              <div className="familyConsentPanel__withdrawn" data-testid="consent-withdrawn-status">
                {isPublicTask
                  ? t('NLSFamilyConsentObjectSuccess')
                  : t('NLSFamilyConsentWithdrawnSuccess')}
              </div>
            ) : (
              <>
                <div className="familyConsentPanel__agreed" data-testid="consent-agreed-status">
                  {t('NLSFamilyConsentAgreedStatus', {
                    level: consent?.level || 'NONE',
                  })}
                </div>

                <div className="familyConsentPanel__actions" style={{ marginTop: 16 }}>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={withdrawing}
                    onClick={() => setShowConfirm(true)}
                    data-testid={isPublicTask ? 'object-consent-btn' : 'withdraw-consent-btn'}
                  >
                    {isPublicTask
                      ? t('NLSFamilyConsentObjectButton')
                      : t('NLSFamilyConsentWithdrawButton')}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </FamilyCard>

      <AppDialog
        open={showConfirm}
        onDismiss={() => setShowConfirm(false)}
        title={
          isPublicTask
            ? t('NLSFamilyConsentConfirmObjectTitle')
            : t('NLSFamilyConsentConfirmTitle')
        }
        size="sm"
        actions={[
          {
            label: t('NLSFamilyConsentConfirmCancel'),
            variant: 'secondary',
            onClick: () => setShowConfirm(false),
          },
          {
            label: isPublicTask
              ? t('NLSFamilyConsentConfirmObjectBtn')
              : t('NLSFamilyConsentConfirmWithdraw'),
            variant: 'danger',
            onClick: handleConfirmWithdraw,
          },
        ]}
      >
        <p className="familyConsentPanel__confirmBody" data-testid="consent-confirm-message">
          {isPublicTask
            ? t('NLSFamilyConsentConfirmObjectBody')
            : t('NLSFamilyConsentWithdrawConfirmBody30Days')}
        </p>
      </AppDialog>
    </div>
  )
}
